import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { exportImage, getImageAssetUrl } from '../../lib/api'
import type { ExportFormat } from '../../lib/api'
import { toast } from 'sonner'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  imageId: string
  /** Suggested filename without extension */
  imageLabel?: string
}

const FORMAT_OPTIONS: { value: ExportFormat; label: string; description: string }[] = [
  { value: 'png', label: 'PNG', description: 'Sin pérdida, transparencia' },
  { value: 'jpg', label: 'JPG', description: 'Menor tamaño, sin transparencia' },
  { value: 'webp', label: 'WebP', description: 'Mejor compresión moderna' },
]

const SCALE_OPTIONS: { value: 1 | 2 | 3; label: string }[] = [
  { value: 1, label: '1x (original)' },
  { value: 2, label: '2x (doble resolución)' },
  { value: 3, label: '3x (triple resolución)' },
]

/**
 * Rough estimate of output file size in KB given input dimensions and options.
 * Very approximate — just for UI guidance.
 */
function estimateSizeKB(
  format: ExportFormat,
  quality: number,
  scale: number,
  basePx = 1024 * 1024
): number {
  const totalPx = basePx * scale * scale
  const bpp =
    format === 'png'
      ? 3
      : format === 'webp'
      ? (quality / 100) * 1.8
      : (quality / 100) * 2.5
  return Math.round((totalPx * bpp) / 1024)
}

export function ExportModal({ isOpen, onClose, imageId, imageLabel }: ExportModalProps) {
  const [format, setFormat] = useState<ExportFormat>('png')
  const [quality, setQuality] = useState(90)
  const [scale, setScale] = useState<1 | 2 | 3>(1)
  const [isExporting, setIsExporting] = useState(false)

  const estimatedKB = estimateSizeKB(format, quality, scale)
  const estimatedLabel =
    estimatedKB >= 1024
      ? `~${(estimatedKB / 1024).toFixed(1)} MB`
      : `~${estimatedKB} KB`

  const handleExport = async () => {
    setIsExporting(true)
    try {
      let blob: Blob
      try {
        blob = await exportImage(imageId, { format, quality, scale })
      } catch {
        // Backend endpoint may not be deployed yet — fall back to direct download
        const response = await fetch(getImageAssetUrl(imageId))
        if (!response.ok) throw new Error('No se pudo descargar la imagen')
        blob = await response.blob()
      }

      const ext = format
      const filename = `${imageLabel ?? `image-${imageId}`}.${ext}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success('Imagen exportada', { description: filename })
      onClose()
    } catch (err) {
      toast.error('Error al exportar', {
        description: err instanceof Error ? err.message : 'Intenta de nuevo',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Exportar imagen" size="sm">
      <div className="space-y-5">
        {/* Format */}
        <div>
          <label className="label mb-2 block">Formato</label>
          <div className="grid grid-cols-3 gap-2">
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormat(opt.value)}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                  format === opt.value
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span className="font-bold">{opt.label}</span>
                <span className="text-[10px] text-slate-500 text-center leading-tight">
                  {opt.description}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Quality — not relevant for PNG */}
        {format !== 'png' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label">Calidad</label>
              <span className="text-sm font-semibold text-slate-700">{quality}%</span>
            </div>
            <input
              type="range"
              min={60}
              max={100}
              step={5}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full accent-emerald-500"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
              <span>60% (menor peso)</span>
              <span>100% (maxima calidad)</span>
            </div>
          </div>
        )}

        {/* Scale */}
        <div>
          <label className="label mb-2 block">Escala</label>
          <select
            className="input"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value) as 1 | 2 | 3)}
          >
            {SCALE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Estimated size */}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-sm">
          <span className="text-slate-500">Tamaño estimado</span>
          <span className="font-semibold text-slate-700">{estimatedLabel}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            className="btn btn-primary flex items-center gap-2"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExporting ? 'Exportando...' : 'Exportar'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
